import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { getCurrentUser } from 'services/Authentication';
import { HideMode } from 'UI/constants/defaults';
import { loadFromLocalStorage } from 'UI/utils';

/**
 * @typedef {Object} FilterDefinition
 * @property {string} name
 * @property {*} [value]
 * @property {Function} [outboundTransformer]
 * @property {Function} [showWhen]
 * @property {string} [hideMode]
 */

/**
 * @param {Object} filters
 * @param {FilterDefinition[]} config
 * @returns {Object}
 */
export const formatFilters = (filters, config) => {
  return Object.keys(filters).reduce((accumulator, key) => {
    const filter = filters[key];

    if (isNil(filter?.value)) return accumulator;

    const filterConfig = config.find(f => f.name === key);

    return {
      ...accumulator,
      [key]: {
        ...filterConfig,
        value: filterConfig?.outboundTransformer
          ? filterConfig.outboundTransformer(filter.value)
          : filter.value
      }
    };
  }, {});
};

/**
 * @param {Object} options
 * @param {string} options.persistenceKey
 */
export class PersistenceFiltersManager {
  currentFilters = {};

  constructor({ persistenceKey }) {
    this.persistenceKey = persistenceKey;
  }

  /**
   * @param {Object} [filtersToPersist={}]
   * @returns {Object}
   */
  persistFilters = (filtersToPersist = {}) => {
    if (!this.persistenceKey) return filtersToPersist;
    const previousFilters = this.currentFilters;
    if (isEqual(previousFilters, filtersToPersist)) return filtersToPersist;

    localStorage.setItem(this.persistenceKey, JSON.stringify(filtersToPersist));
    this.currentFilters = filtersToPersist;
    return filtersToPersist;
  };

  /**
   * @returns {Object}
   */
  loadPersistedFilters = () => {
    return loadFromLocalStorage(this.persistenceKey, {});
  };

  resetFilters = () => {
    this.persistFilters({});
  };
}

/**
 * @param {FilterDefinition} filter
 * @param {Object} filters
 * @returns {(boolean|null)}
 */
export const ensureIfFilterIsActive = (filter, filters) => {
  const user = getCurrentUser();
  const isVisible = filter.showWhen !== undefined ? filter.showWhen(filters, user) : true;

  if (!isVisible && (!filter.hideMode || filter.hideMode === HideMode.Unmounted)) {
    return null;
  }

  return isVisible;
};
